import {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';

type Metadata = {title: string; description: string; canonical: string | null; robots: string;
    image: string | null; type: string; site_name: string; structured_data: unknown[]; track: boolean};
const initialPath = window.location.pathname;
const initial = JSON.parse(document.querySelector('meta[name="page-metadata"]')?.getAttribute('content') || '{}') as Metadata;
const measurementId = document.querySelector('meta[name="analytics-id"]')?.getAttribute('content');
const consentKey = 'analytics-consent';
let lastPage: string | null = null;
let previousPage = '';
let loaded = false;
let consent = readConsent();

function readConsent(): string | null {
    try { return localStorage.getItem(consentKey); } catch { return null; }
}
function gtag(...args: unknown[]) {
    const w = window as typeof window & {dataLayer?: unknown[]};
    w.dataLayer = w.dataLayer || [];
    // gtag expects Arguments entries, not arrays.
    w.dataLayer.push(arguments);
}
function enabled() {
    return consent === 'accepted' && navigator.doNotTrack !== '1' &&
        !(navigator as Navigator & {globalPrivacyControl?: boolean}).globalPrivacyControl;
}
function trackPage(data: Metadata) {
    if (!measurementId || !enabled() || !data.track || !data.canonical) return;
    if (!loaded) {
        loaded = true;
        gtag('js', new Date());
        gtag('config', measurementId, {send_page_view: false, allow_google_signals: false,
            allow_ad_personalization_signals: false, page_location: data.canonical, page_referrer: ''});
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
        document.head.appendChild(script);
    }
    if (lastPage === data.canonical) return;
    gtag('event', 'page_view', {send_to: measurementId, page_title: data.title,
        page_location: data.canonical, page_referrer: previousPage});
    lastPage = data.canonical;
    previousPage = data.canonical;
}

export function trackConversion(event: 'generate_lead', kind: string) {
    if (!loaded || !enabled() || !measurementId || !lastPage) return;
    gtag('event', event, {send_to: measurementId, lead_type: kind, page_location: lastPage, page_referrer: ''});
}

function meta(attribute: 'name' | 'property', key: string, value?: string | null) {
    let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
    if (!value) { element?.remove(); return; }
    if (!element) { element = document.createElement('meta'); element.setAttribute(attribute, key); document.head.appendChild(element); }
    element.content = value;
}
function apply(data: Metadata) {
    document.title = data.title;
    meta('name', 'description', data.description);
    meta('name', 'robots', data.robots);
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (data.canonical) {
        if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
        canonical.href = data.canonical;
    } else canonical?.remove();
    for (const [key, value] of Object.entries({title: data.title, description: data.description, url: data.canonical,
        type: data.type, site_name: data.site_name, image: data.image})) meta('property', `og:${key}`, value);
    meta('name', 'twitter:card', data.image ? 'summary_large_image' : 'summary');
    meta('name', 'twitter:title', data.title);
    meta('name', 'twitter:description', data.description);
    meta('name', 'twitter:image', data.image);
    const structured = document.getElementById('seo-structured-data');
    if (structured) structured.textContent = JSON.stringify(data.structured_data);
}

export default function PageMetadata() {
    const {pathname} = useLocation();
    const [choice, setChoice] = useState(consent);
    const [data, setData] = useState<Metadata | null>(null);
    const [settings, setSettings] = useState(false);
    useEffect(() => {
        const controller = new AbortController();
        setData(null);
        // Clear the previous page immediately so private and failed routes cannot retain public tags.
        apply({title: initial.site_name || initial.title, description: '', canonical: null, robots: 'noindex, follow',
            image: null, type: 'website', site_name: initial.site_name, structured_data: [], track: false});
        const update = (next: Metadata) => {
            if (controller.signal.aborted) return;
            apply(next); setData(next);
            if (!next.track) lastPage = null;
            trackPage(next);
        };
        if (pathname === initialPath) update(initial);
        else fetch(`/metadata?path=${encodeURIComponent(pathname)}`, {signal: controller.signal})
            .then(response => { if (!response.ok && response.status !== 404) throw new Error('Metadata unavailable'); return response.json(); })
            .then(update).catch(() => { /* Leave noindex on failed metadata requests. */ });
        return () => controller.abort();
    }, [pathname]);
    function choose(value: string) {
        consent = value; setChoice(value); setSettings(false);
        try { localStorage.setItem(consentKey, value); } catch { /* Choice applies to this visit. */ }
        const w = window as unknown as Record<string, unknown>;
        if (measurementId) w[`ga-disable-${measurementId}`] = value !== 'accepted';
        if (value === 'accepted' && data) trackPage(data);
        else {
            lastPage = null;
            // Remove GA cookies on this host and parent domain when consent is withdrawn.
            document.cookie.split(';').forEach(cookie => {
                const name = cookie.split('=')[0].trim();
                if (!/^_ga(?:_|$)/.test(name)) return;
                document.cookie = `${name}=; Max-Age=0; path=/`;
                const parts = location.hostname.split('.');
                for (let i = 0; i < parts.length - 1; i++) document.cookie = `${name}=; Max-Age=0; path=/; domain=${parts.slice(i).join('.')}`;
            });
        }
    }
    if (!measurementId || !data?.track) return null;
    if (choice && !settings) return <button type="button" onClick={() => setSettings(true)} className="fixed bottom-2 left-2 z-[100] bg-gray-950 text-white border border-gray-600 rounded px-3 py-2 text-xs">Analytics preferences</button>;
    return <aside aria-label="Analytics preferences" className="fixed bottom-4 left-4 right-4 z-[100] bg-gray-950 text-white border border-gray-600 rounded p-4 max-w-xl shadow-xl">
        <p>Allow optional Google Analytics cookies to help us understand public page visits and booking requests? <a href="/privacy" className="underline">Privacy policy</a></p>
        <div className="flex gap-3 mt-3">
            <button type="button" onClick={() => choose('declined')} className="border rounded px-4 py-2">Decline</button>
            <button type="button" onClick={() => choose('accepted')} className="border rounded px-4 py-2">Allow analytics</button>
        </div>
    </aside>;
}
